/**
 * The plan tab "Kết nối module" (ADR-032 §2.11), laid out as a TLD's readiness: 1 the module, 2 a
 * server group of that module with what each member reports, 3 the provider's plan (read from the
 * servers when the module lists its plans), 4 the placement settings the plan overrides, 5
 * "Kiểm tra" (snapshots) and "Kiểm tra ngay" (asks every member), 6 "Sẵn sàng bán": each condition
 * passed or not, with where to fix it.
 */
import { CheckCircle2, CircleAlert, ListChecks, Pencil, Zap } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, isApiError } from '../../api';
import type { FulfilCheck, FulfilmentCheckResult, FulfilProblem, PlanDetail } from '../../api/areas/catalog';
import type { ModuleBranchId, ModuleSummary } from '../../api/areas/modules';
import { Badge } from '../../components/Badge';
import { Callout, Field, Section } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { EmptyState, Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { useAccess } from '../../lib/access';
import { fulfilReadiness, type FulfilCondition } from '../../lib/catalog';
import { formatDateTime } from '../../lib/format';
import { useApi, type UseApiResult } from '../../lib/useApi';
import type { FormValue } from '../modules/fieldKinds';
import { useText, useValueText } from '../modules/ui';
import { ActionDialog, FormFooter, ReasonField, selectClass, type DialogBodyProps } from '../operator/shared';
import { useCatalogT } from './editorKit';
import { MemberFactsTable, memberKeys, MemberListSelect, PlacementFields, placementPayload, placementValues, useMemberFacts, useModuleSummaries } from './moduleKit';

const STATE_ICON = { OK: CheckCircle2, WARN: CircleAlert, BLOCK: CircleAlert } as const;
const STATE_TONE = { OK: 'text-success', WARN: 'text-warning', BLOCK: 'text-destructive' } as const;
const linkClass = 'font-medium text-primary underline-offset-4 hover:underline';

/** Every problem of a check, whatever way of delivering the plan it was found on (the table groups them by condition). */
function problemsOf(checks: readonly FulfilCheck[]): FulfilProblem[] {
  return checks.flatMap((c) => c.problems);
}

export function PlanModuleTab({ d, onChanged }: { d: PlanDetail; onChanged: () => void }) {
  const { t } = useCatalogT();
  if (d.product.fulfilment === 'MANUAL' || !d.product.branch) {
    return (
      <Section>
        <Callout tone="info">{t('delivery.manual')}</Callout>
      </Section>
    );
  }
  return <ModuleTab d={d} branch={d.product.branch} onChanged={onChanged} />;
}

function ModuleTab({ d, branch, onChanged }: { d: PlanDetail; branch: ModuleBranchId; onChanged: () => void }) {
  const { t, name } = useCatalogT();
  const text = useText();
  const valueText = useValueText();
  const f = d.fulfilment;
  const group = f ? d.placement_groups.find((g) => g.code === f.placement_group) : undefined;
  const modules = useModuleSummaries(branch);
  const summary = modules?.find((m) => m.manifest.id === f?.module) ?? null;
  const manifest = summary?.manifest ?? null;
  const facts = useMemberFacts(branch, manifest, memberKeys(group?.members ?? [], f?.module ?? null, false));
  const [editing, setEditing] = useState(false);
  // The check runs from the snapshots when the tab opens; "Kiểm tra" runs it again, "Kiểm tra ngay" asks every member.
  const [run, setRun] = useState({ live: false, n: 0 });
  const check = useApi((signal) => (f ? api.checkPlanFulfilment(d.plan.id, run.live, { signal }) : Promise.resolve(null)), [d.plan.id, f?.version, run.n]);
  const recheck = (live: boolean) => setRun((x) => ({ live, n: x.n + 1 }));
  const field = (key: string) => manifest?.placement_settings.fields.find((x) => x.key === key);
  return (
    <div className="grid gap-4">
      <Section
        title={t('planTabs.delivery')}
        actions={
          d.can.manage ? (
            <ActionDialog label={t('delivery.edit')} icon={Pencil} title={t('delivery.editTitle', { plan: d.plan.code })} wide open={editing} onOpenChange={setEditing} onChanged={onChanged}>
              {(p) => <ModuleConnectionForm {...p} d={d} branch={branch} modules={modules} onStale={() => recheck(false)} />}
            </ActionDialog>
          ) : null
        }
      >
        {f ? (
          <div className="grid gap-4">
            <KeyValue
              columns={2}
              rows={[
                [t('delivery.module'), manifest ? text(manifest.name) : <span key="module" className="font-mono">{f.module}</span>],
                [t('delivery.group'), group ? name(group) : f.placement_group],
                [t('delivery.providerPlan'), f.provider_plan ? <span key="plan" className="font-mono">{f.provider_plan}</span> : t('delivery.noProviderPlan')],
                f.provider_template ? [t('delivery.template'), <span key="template" className="font-mono">{f.provider_template}</span>] : null,
                [
                  t('delivery.overrides'),
                  Object.keys(f.module_settings).length ? (
                    <ul key="overrides" className="grid gap-0.5">
                      {Object.entries(f.module_settings).map(([k, v]) => {
                        const fd = field(k);
                        return (
                          <li key={k}>
                            {fd ? text(fd.label) : <span className="font-mono text-xs">{k}</span>}: {fd ? valueText(fd, v) : String(v)}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    t('delivery.noOverrides')
                  ),
                ],
              ]}
            />
            {group ? <MemberFactsTable branch={branch} members={group.members.filter((m) => m.module === f.module)} summary={summary} facts={facts} /> : null}
          </div>
        ) : (
          <EmptyState title={t('delivery.none')} />
        )}
      </Section>
      {f ? <Readiness d={d} check={check} onCheck={recheck} onEdit={d.can.manage ? () => setEditing(true) : null} /> : null}
    </div>
  );
}

/** "Sẵn sàng bán" (ADR-032 §2.9, §2.11): each condition of the check, passed or not, with where to fix it. */
function Readiness({ d, check, onCheck, onEdit }: { d: PlanDetail; check: UseApiResult<FulfilmentCheckResult | null>; onCheck: (live: boolean) => void; onEdit: (() => void) | null }) {
  const { t } = useCatalogT();
  // Asking the members is a call to the servers: `integration.read` as well (ADR-032 §2.12).
  const live = useAccess().has(['integration.read']);
  const r = check.status === 'success' ? check.data : null;
  const blocked = r ? !r.ok : false;
  const fix = (condition: FulfilCondition): ReactNode => {
    switch (condition) {
      case 'module':
      case 'plan':
      case 'settings':
        return onEdit ? (
          <Button type="button" size="xs" variant="outline" onClick={onEdit}>
            {t('fulfil.fix.edit')}
          </Button>
        ) : null;
      case 'options':
        return (
          <Link to={`/products?tab=product-catalog&sub=products&product=${encodeURIComponent(d.product.id)}&view=options`} className={linkClass}>
            {t('fulfil.fix.options')}
          </Link>
        );
      default:
        return (
          <Link to="/products?tab=product-catalog&sub=server-groups" className={linkClass}>
            {t('fulfil.fix.group')}
          </Link>
        );
    }
  };
  return (
    <Section
      title={
        <span className="flex flex-wrap items-center gap-2">
          {t('fulfil.title')} {r ? <Badge tone={blocked ? 'warn' : 'ok'}>{blocked ? t('fulfil.notReady') : t('fulfil.ready')}</Badge> : null}
        </span>
      }
      description={r ? t(`fulfil.checked.${r.source}`, { when: formatDateTime(r.checked_at) }) : t('fulfil.intro')}
      actions={
        <>
          <Button type="button" size="sm" variant="outline" disabled={check.status === 'loading'} onClick={() => onCheck(false)}>
            <ListChecks aria-hidden="true" />
            {t('fulfil.check')}
          </Button>
          {live ? (
            <Button type="button" size="sm" variant="outline" disabled={check.status === 'loading'} onClick={() => onCheck(true)}>
              <Zap aria-hidden="true" />
              {t('fulfil.checkLive')}
            </Button>
          ) : null}
        </>
      }
    >
      {check.status === 'loading' ? <Loading rows={3} /> : null}
      {check.status === 'error' ? <DataError error={check.error} onRetry={check.reload} /> : null}
      {r ? <ReadinessRows problems={problemsOf(r.checks)} fix={fix} /> : null}
    </Section>
  );
}

/** The conditions of the check, each with its problems (code in words, the member it is about, what it names). */
function ReadinessRows({ problems, fix }: { problems: readonly FulfilProblem[]; fix?: (c: FulfilCondition) => ReactNode }) {
  const { t } = useCatalogT();
  return (
    <ul className="grid gap-2">
      {fulfilReadiness(problems).map((row) => {
        const Icon = STATE_ICON[row.state];
        return (
          <li key={row.condition} className="grid gap-1.5 rounded-lg border p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <span className="flex items-start gap-2">
                <Icon className={`mt-0.5 size-4 shrink-0 ${STATE_TONE[row.state]}`} aria-hidden="true" />
                <span className="grid gap-0.5">
                  <span className="text-sm font-medium">
                    {t(`fulfil.condition.${row.condition}`)} <span className="text-xs font-normal text-muted-foreground">· {t(`fulfil.state.${row.state}`)}</span>
                  </span>
                  {row.problems.map((p, i) => (
                    <span key={`${p.code}-${p.server ?? ''}-${i}`} className="text-xs text-muted-foreground">
                      {t(`fulfil.problem.${p.code}`, { defaultValue: p.code })}
                      {p.server ? ` · ${p.server}` : ''}
                      {p.detail ? ` · ${p.detail}` : ''}
                    </span>
                  ))}
                </span>
              </span>
              {row.state !== 'OK' && fix ? <span className="flex items-center gap-2">{fix(row.condition)}</span> : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * "Sửa kết nối module": the module, a server group of that module, the provider's plan (from every
 * accepting member when the module lists its plans), the template, and the overrides of the
 * group's placement settings (an empty field follows the group). Saving runs the check from the
 * snapshots: 422 FULFILMENT_INVALID shows its problems; 409 FULFILMENT_CHANGED checks again.
 */
function ModuleConnectionForm({ close, changed, d, branch, modules, onStale }: DialogBodyProps & { d: PlanDetail; branch: ModuleBranchId; modules: ModuleSummary[] | null | undefined; onStale: () => void }) {
  const { t, name } = useCatalogT();
  const text = useText();
  const f = d.fulfilment;
  // The connections are listed with `integration.read`; while they load the module list waits, and
  // when they cannot be read the module, the package and the settings are typed.
  const canList = useAccess().has(['integration.read']);
  const [module, setModule] = useState(f?.module ?? '');
  // ADR-032 §2.4: only the groups of the chosen module.
  const groups = d.placement_groups.filter((g) => g.module === module);
  const [group, setGroup] = useState(f?.placement_group ?? '');
  const chosen = groups.find((g) => g.code === group);
  const summary = modules?.find((m) => m.manifest.id === module) ?? null;
  const manifest = summary?.manifest ?? null;
  // Lists read the members taking new services; the table shows every member of the module.
  const keys = memberKeys(chosen?.members ?? [], module, true);
  const facts = useMemberFacts(branch, manifest, memberKeys(chosen?.members ?? [], module, false));
  const [plan, setPlan] = useState(f?.provider_plan ?? '');
  const [template, setTemplate] = useState(f?.provider_template ?? '');
  const fields = manifest?.placement_settings.fields ?? [];
  const [typed, setTyped] = useState<Record<string, FormValue>>({});
  const values = { ...placementValues(fields, module === f?.module ? f.module_settings : {}, true), ...typed };
  const overrides = placementPayload(fields, values, true);
  const listed = manifest?.plan_source ?? null;
  const [reason, setReason] = useState('');
  const w = useWrite(async (b: { reason: string }, opts) => {
    try {
      // Only the overrides typed (§2.5). Without the module's manifest the stored ones stay (§4.2),
      // unless the module changes: they were the other module's.
      const settings = manifest ? overrides.payload : module !== f?.module ? {} : undefined;
      const body = { version: f?.version ?? null, module, placement_group: group, provider_plan: plan.trim() || null, provider_template: template.trim() || null, module_settings: settings, reason: b.reason };
      return await api.setPlanFulfilment(d.plan.id, body, opts);
    } catch (e) {
      // §2.9: a group or the connection changed while it was checked: check again, reload on close.
      if (isApiError(e) && e.code === 'FULFILMENT_CHANGED') {
        onStale();
        changed();
      }
      throw e;
    }
  });
  const error = w.state.phase === 'error' ? w.state.error : null;
  const problems = isApiError(error) && error.code === 'FULFILMENT_INVALID' ? problemsOf((error.details as { checks?: FulfilCheck[] } | null)?.checks ?? []) : [];
  const pickModule = (m: string) => {
    setModule(m);
    setGroup('');
    setPlan('');
    setTyped({});
  };
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      {w.state.phase === 'done' ? (
        <Callout tone="ok">{t('applied')}</Callout>
      ) : (
        <>
          {canList ? null : <Callout tone="info">{t('delivery.noModuleAccess')}</Callout>}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('delivery.step.module')}>
              {modules === undefined ? (
                <select className={selectClass} disabled>
                  <option>…</option>
                </select>
              ) : modules ? (
                <select className={selectClass} value={module} onChange={(e) => pickModule(e.target.value)} required>
                  <option value="">{t('connection.choose')}</option>
                  {module && !summary ? <option value={module}>{module}</option> : null}
                  {modules.map((m) => (
                    <option key={m.manifest.id} value={m.manifest.id}>
                      {text(m.manifest.name)}
                    </option>
                  ))}
                </select>
              ) : (
                <Input value={module} onChange={(e) => pickModule(e.target.value.trim().toLowerCase())} className="font-mono" required />
              )}
            </Field>
            <Field label={t('delivery.step.group')} hint={module && groups.length === 0 ? t('delivery.noGroup') : undefined}>
              <select
                className={selectClass}
                value={group}
                onChange={(e) => {
                  setGroup(e.target.value);
                  setTyped({});
                }}
                required
              >
                <option value="">{t('connection.choose')}</option>
                {groups.map((g) => (
                  <option key={g.code} value={g.code}>
                    {name(g)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {chosen ? <MemberFactsTable branch={branch} members={chosen.members.filter((m) => m.module === module)} summary={summary} facts={facts} /> : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('delivery.step.plan')} hint={listed ? t('delivery.providerPlanListed') : t('delivery.providerPlanHint')}>
              {listed ? (
                <MemberListSelect branch={branch} module={module} keys={keys} action={listed} value={plan} onChange={setPlan} empty={t('connection.choose')} facts={facts} />
              ) : (
                <Input value={plan} onChange={(e) => setPlan(e.target.value)} className="font-mono" />
              )}
            </Field>
            <Field label={t('delivery.template')} hint={t('delivery.templateHint')}>
              <Input value={template} onChange={(e) => setTemplate(e.target.value)} className="font-mono" />
            </Field>
          </div>
          {fields.length && chosen ? (
            <fieldset className="grid gap-3 rounded-md border p-3">
              <legend className="px-1 text-sm font-medium">{t('delivery.step.settings')}</legend>
              <p className="text-xs text-muted-foreground">{t('delivery.settingsHint')}</p>
              <PlacementFields fields={fields} values={values} onChange={(k, v) => setTyped((x) => ({ ...x, [k]: v }))} defaults={chosen.module_settings} list={{ branch, module, keys, facts }} />
            </fieldset>
          ) : null}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {problems.length ? <ReadinessRows problems={problems} /> : null}
      {error ? <WriteError error={error} /> : null}
      <FormFooter busy={w.busy} disabled={!module || !group || (listed !== null && !plan) || !overrides.valid || !reason.trim()} submit={w.state.phase === 'done' ? undefined : t('save')} onClose={close} />
    </form>
  );
}
